import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PencilIcon, TrashBinIcon, PlusIcon, ArrowUpIcon, ArrowDownIcon } from '@/icons';
import { RowActionsMenu } from '@/components/admin/RowActionsMenu';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import { DraggableRow, DragHandle } from '@/components/cccd/DraggableRow';
import { useDragReorder } from '@/hooks/useDragReorder';
import { cccdAdmin, type EventPlan } from '@/lib/api/cccdAdminClient';
import { applyOptimistic, rollbackOptimistic } from '@/lib/optimisticMutation';
import { showUndoToast } from '@/lib/undoToast';
import { toastError } from '@/lib/toast';
import type { CategoryParent } from '@/components/cccd/EventCategoriesSection';
import { StatusPill } from '@/components/cccd/StatusPill';
import { LoadState } from "@/components/ui/LoadState"

const PLAN_TYPE_LABELS: Record<string, string> = {
  full: 'Pago único',
  installments: 'Cuotas fijas',
  flexible: 'Meses flexible',
};

const ITEM_TYPE = 'cccd-plan-row';

// Reused unmodified by RegionalShell's Configuración tab — see
// EventCategoriesSection for the parent-discriminator rationale, and for why
// this section does NOT wrap its own <DndProvider> (the caller shares one).
export function EventPlansSection({ parent }: { parent: CategoryParent }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const parentId = parent.type === 'event' ? parent.eventId : parent.regionalId;
  const basePath = parent.type === 'event' ? `/cccd/events/${parentId}` : `/cccd/regionals/${parentId}`;
  const queryKey = ['event-plans', parent.type, parentId];

  const { data: plans = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => (parent.type === 'event' ? cccdAdmin.getEventPlans(parentId) : cccdAdmin.getRegionalPlans(parentId)),
  });

  const { moveRow, onDragStart, onDrop, moveBy } = useDragReorder<EventPlan>(
    queryKey,
    (planId, orderIndex) =>
      parent.type === 'event'
        ? cccdAdmin.updateEventPlan(parentId, planId, { orderIndex })
        : cccdAdmin.updateRegionalPlan(parentId, planId, { orderIndex }),
  );

  const invalidate = () => qc.invalidateQueries({ queryKey });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      parent.type === 'event' ? cccdAdmin.deleteEventPlan(parentId, id) : cccdAdmin.deleteRegionalPlan(parentId, id),
  });

  const handleDelete = async (plan: EventPlan) => {
    const previous = await applyOptimistic<EventPlan[]>(qc, queryKey, (prev) =>
      (prev ?? []).filter((p) => p.id !== plan.id),
    );
    showUndoToast({
      message: `Plan "${plan.name}" eliminado`,
      onUndo: () => rollbackOptimistic(qc, queryKey, previous),
      onCommit: async () => {
        try {
          await deleteMutation.mutateAsync(plan.id);
          invalidate();
        } catch {
          rollbackOptimistic(qc, queryKey, previous);
          toastError('No se pudo eliminar el plan.');
        }
      },
    });
  };

  return (
    <SectionHeader
      title="Planes de pago"
      subtitle="Cuotas, interés y descuento por pago anticipado"
      icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></svg>}
    >
      <button
        type="button"
        onClick={() => navigate(`${basePath}/plans/new`)}
        className="flex w-full items-center gap-2.5 rounded-[14px] border-[1.5px] border-dashed border-gray-300 bg-gray-25 min-h-11 px-3.5 py-3.5 text-gray-500 transition-colors hover:border-brand-300 hover:text-brand-500 dark:border-gray-700 dark:bg-white/[0.02] dark:hover:border-brand-700"
      >
        <PlusIcon className="size-4" />
        <span className="text-[13px] font-medium">Agregar plan nuevo</span>
      </button>

      {isLoading ? (
        <LoadState />
      ) : (
        <div className="-mx-6 mt-4 overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr>
              {['', 'Plan', 'Cuotas / interés', 'Pago anticipado', 'Estado', ''].map((h, i) => (
                <th key={i} className="border-t border-gray-100 px-6 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 dark:border-gray-800">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {plans.map((plan, index) => (
              <DraggableRow key={plan.id} id={plan.id} index={index} itemType={ITEM_TYPE} moveRow={moveRow} onDragStart={onDragStart} onDrop={onDrop}>
                <td className="w-6 py-3.5 pl-6"><DragHandle /></td>
                <td className="px-2 py-3.5 font-medium text-gray-800 dark:text-white/90">{plan.name}</td>
                <td className="px-2 py-3.5 text-[13px] text-gray-600 dark:text-gray-400">
                  {PLAN_TYPE_LABELS[plan.planType] ?? plan.planType} · {plan.interestRatePercent}% interés
                </td>
                <td className="px-2 py-3.5 text-[13px] text-gray-600 dark:text-gray-400">{plan.earlyBirdDiscountPercent}%</td>
                <td className="px-2 py-3.5">
                  <StatusPill tone={plan.isActive ? 'success' : 'gray'} label={plan.isActive ? 'Activo' : 'Inactivo'} />
                </td>
                <td className="py-3.5 pr-6 text-right">
                  <RowActionsMenu
                    actions={[
                      {
                        label: 'Editar',
                        icon: <PencilIcon />,
                        to: `${basePath}/plans/${plan.id}/edit`,
                      },
                      // El arrastre no funciona al tacto ni con teclado: estas acciones reordenan igual.
                      { label: 'Subir', icon: <ArrowUpIcon />, onClick: () => void moveBy(index, -1), disabled: index === 0 },
                      { label: 'Bajar', icon: <ArrowDownIcon />, onClick: () => void moveBy(index, 1), disabled: index === plans.length - 1 },
                      {
                        label: 'Eliminar',
                        icon: <TrashBinIcon />,
                        onClick: () => handleDelete(plan),
                        variant: 'danger',
                      },
                    ]}
                  />
                </td>
              </DraggableRow>
            ))}
            {plans.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  No hay planes. Añade uno para comenzar.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      )}
    </SectionHeader>
  );
}
